import { ReleaseFlag } from "@/utility/feature-management";

export interface PermissionGroup {
  key: string;
  permissions: readonly string[];
  featureFlag: ReleaseFlag | undefined;
  parentPermission?: string;
  permissionParents?: Partial<Record<string, string>>;
  permissionFlags?: Partial<Record<string, ReleaseFlag>>;
}

export type PermissionCategories = Record<string, {
  subCategories: readonly PermissionGroup[];
}>;

export interface Role {
  name: string;
  permissions: string[];
}

export interface RolesAndPermissionsData {
  roles: Role[];
  allPermissions: string[];
  permissionMatrix: Record<string, string[]>;
}

// Add new feature flags here and then under each featureFlag in the PERMISSION_CATEGORIES object
// after that ts errors should guide us on what code needs updating
export const RBAC_FLAG_CONFIG = {
  systemAccess: ReleaseFlag.releaseRbacPermissionsSystemAccess,
  checkInOut: ReleaseFlag.releaseRbacPermissionsCheckInOut,
  bookingUncancel: ReleaseFlag.releaseBookingUncancel,
  printBookingDetails: ReleaseFlag.releasePrintBookingDetailsPermissions,
  productPackages: ReleaseFlag.releaseRbacPermissionsProductPackages,
  insights: ReleaseFlag.releaseRbacPermissionsInsights,
  insightsCategories: ReleaseFlag.releaseRbacPermissionsInsightsCategories,
  driverShiftTrackerPermissions: ReleaseFlag.releaseRbacPermissionsDriverShiftTracker,
  locationsManagement: ReleaseFlag.releaseRbacLocationsManagement,
  lanePlanning: ReleaseFlag.releaseRbacLanePlanningPermissions,
  updateBookingDates: ReleaseFlag.releaseRbacPermissionEditingBookingStartAndEndDate,
} as const;

export type RbacFlagKey = keyof typeof RBAC_FLAG_CONFIG;
export type RbacFlags = {
  [K in RbacFlagKey]: boolean;
};

export const PERMISSION_CATEGORIES = {
  systemAccess: {
    subCategories: [
      {
        key: "",
        permissions: [
          "portal.mobile.access",
          "portal.web.access",
          "portal.kiosk.access",
          "portal.locker.access",
        ],
        featureFlag: RBAC_FLAG_CONFIG.systemAccess
      },
    ],
  },
  bookingManagement: {
    subCategories: [
      {
        key: "checkingInAndOut",
        permissions: [
          "bookingManagement.booking.read",
          "bookingManagement.booking.update",
          "bookingManagement.booking.updateBookingDates",
          "bookingManagement.booking.updateStatus",
          "bookingManagement.booking.revert",
          "bookingManagement.booking.create",
          "bookingManagement.booking.cancel",
          "bookingManagement.data.retain",
        ],
        featureFlag: RBAC_FLAG_CONFIG.checkInOut,
        permissionParents: {
          "bookingManagement.booking.updateBookingDates": "bookingManagement.booking.update",
        },
        permissionFlags: {
          "bookingManagement.booking.updateBookingDates": RBAC_FLAG_CONFIG.updateBookingDates,
        },
      },
      {
        key: "productPackageAndUpsell",
        permissions: [
          "bookingManagement.saleItems.create",
          "bookingManagement.saleItems.read",
          "bookingManagement.saleItems.update",
          "bookingManagement.saleItems.delete",
          "bookingManagement.upsells.create",
          "bookingManagement.upsells.read",
          "bookingManagement.upsells.update",
          "bookingManagement.upsells.delete"
        ],
        featureFlag: RBAC_FLAG_CONFIG.productPackages
      },
      {
        key: "printBookingDetails",
        permissions: [
          "bookingManagement.booking.printDetails",
        ],
        featureFlag: RBAC_FLAG_CONFIG.printBookingDetails
      },
    ],
  },
  insights: {
    subCategories: [
      {
        key: "",
        permissions: [
          "web.insights.read",
        ],
        featureFlag: RBAC_FLAG_CONFIG.insights,
      }
    ]
  },
  assetTracking: {
    subCategories: [
      {
        key: "locationManager",
        permissions: [
          "assetTracking.location.manage",
          "assetTracking.location.updateStatus",
          "assetTracking.location.reorder",
        ],
        featureFlag: RBAC_FLAG_CONFIG.locationsManagement
      },
    ],
  },
  lanePlanning: {
    subCategories: [
      {
        key: "lanePlanning",
        permissions: [
          "lanePlanning.planning.view",
          "lanePlanning.rule.update"
        ],
        featureFlag: RBAC_FLAG_CONFIG.lanePlanning
      },
    ],
  },
  vehicleCondition: {
    subCategories: [
      {
        key: "",
        permissions: [],
        featureFlag: undefined
      },
    ],
  },
  buses: {
    subCategories: [
      {
        key: "",
        permissions: [],
        featureFlag: undefined
      },
    ],
  },
  staffManagement: {
    subCategories: [
      {
        key: "driverShiftTracker",
        permissions: [
          "shiftTracking.shift.view",
          "shiftTracking.shift.update"
        ],
        featureFlag: RBAC_FLAG_CONFIG.driverShiftTrackerPermissions
      }
    ]
  },
} as const satisfies PermissionCategories;

export const PERMISSION_CATEGORIES_V2 = {
  ...PERMISSION_CATEGORIES,
  bookingManagement: {
    subCategories: [
      {
        ...PERMISSION_CATEGORIES.bookingManagement.subCategories[0],
        permissions: [
          "bookingManagement.booking.read",
          "bookingManagement.booking.update",
          "bookingManagement.booking.updateBookingDates",
          "bookingManagement.booking.updateStatus",
          "bookingManagement.booking.revert",
          "bookingManagement.booking.create",
          "bookingManagement.booking.cancel",
          "bookingManagement.booking.uncancel",
          "bookingManagement.data.retain",
        ],
        permissionFlags: {
          "bookingManagement.booking.uncancel": RBAC_FLAG_CONFIG.bookingUncancel,
          "bookingManagement.booking.updateBookingDates": RBAC_FLAG_CONFIG.updateBookingDates,
        },
      },
      ...PERMISSION_CATEGORIES.bookingManagement.subCategories.slice(1),
    ],
  },
  insights: {
    subCategories: [
      {
        key: "",
        permissions: [
          "web.insights.read",
          "web.insights.realtime.read",
          "web.insights.bookings.read",
          "web.insights.movements.read",
          "web.insights.occupancy.read",
          "web.insights.staffActivity.read",
        ],
        featureFlag: RBAC_FLAG_CONFIG.insights,
        parentPermission: "web.insights.read",
        permissionFlags: {
          "web.insights.realtime.read": RBAC_FLAG_CONFIG.insightsCategories,
          "web.insights.bookings.read": RBAC_FLAG_CONFIG.insightsCategories,
          "web.insights.movements.read": RBAC_FLAG_CONFIG.insightsCategories,
          "web.insights.occupancy.read": RBAC_FLAG_CONFIG.insightsCategories,
          "web.insights.staffActivity.read": RBAC_FLAG_CONFIG.insightsCategories,
        },
      }
    ]
  },
} as const satisfies PermissionCategories;

export type PermissionCategoryKey = keyof typeof PERMISSION_CATEGORIES;
export type PermissionSubCategory = PermissionGroup["key"];
export type Permission = (typeof PERMISSION_CATEGORIES_V2)[keyof typeof PERMISSION_CATEGORIES_V2]["subCategories"][number]["permissions"][number];
export type PermissionFlag = PermissionGroup["featureFlag"];

export const getPermissionFlag = (
  subCategory: PermissionGroup,
  permission: string
): ReleaseFlag | undefined => {
  return subCategory.permissionFlags?.[permission] ?? subCategory.featureFlag;
};

export const getParentPermission = (
  subCategory: PermissionGroup,
  permission: string
): string | undefined => {
  const explicitParent = subCategory.permissionParents?.[permission];
  if (explicitParent) {
    return explicitParent;
  }

  if (subCategory.parentPermission && permission !== subCategory.parentPermission) {
    return subCategory.parentPermission;
  }

  return undefined;
};

const permissionGroups = (Object.values(PERMISSION_CATEGORIES_V2) as Array<{ subCategories: readonly PermissionGroup[] }>)
  .flatMap((category) => category.subCategories);

export const permissionsToFlagMap = new Map<Permission, ReleaseFlag>(
  permissionGroups
    .flatMap((subCategory) =>
      subCategory.permissions.flatMap((permission) => {
        const flag = getPermissionFlag(subCategory, permission);
        if (!flag) {
          return [];
        }

        return [[permission as Permission, flag] as const];
      })
    )
);

export const permissionParentMap = new Map<Permission, Permission>(
  permissionGroups.flatMap((subCategory) =>
    subCategory.permissions.flatMap((permission) => {
      const parentPermission = getParentPermission(subCategory, permission);

      if (!parentPermission) {
        return [];
      }

      return [[permission as Permission, parentPermission as Permission] as const];
    })
  )
);

export const getPermissionParent = (permission: Permission): Permission | undefined =>
  permissionParentMap.get(permission);
